import React, { useEffect, useMemo, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { useBuildingModel } from "./hooks/useBuildingModel";
import { useDropsMaterialUpdater } from "./hooks/useDropsMaterialUpdater";
import {
  reachAndWashMaterial,
  standardCleanedMaterial,
  standardFaultyMaterial,
} from "./materials";

const SHARED_STATUS_MATERIALS = new Set([
  reachAndWashMaterial,
  standardCleanedMaterial,
  standardFaultyMaterial,
]);

const disposeMaterial = (material) => {
  if (!material || SHARED_STATUS_MATERIALS.has(material)) {
    return;
  }

  Object.values(material).forEach((value) => {
    if (value && value.isTexture) {
      value.dispose();
    }
  });

  material.dispose?.();
};

const disposeSceneResources = (scene) => {
  if (!scene) {
    return;
  }

  scene.traverse((node) => {
    if (!node?.isMesh) {
      return;
    }

    node.geometry?.dispose?.();

    if (Array.isArray(node.material)) {
      node.material.forEach(disposeMaterial);
      return;
    }

    disposeMaterial(node.material);
  });
};

export default function Model({
  siteId,
  onError,
  drops = [],
  showStatusColors = false,
  lowPerformanceMode = false,
}) {
  const { modelPath } = useBuildingModel(siteId, onError, {
    preferLowPoly: lowPerformanceMode,
    timeoutMs: lowPerformanceMode ? 16000 : 22000,
  });

  if (!modelPath) {
    return null;
  }

  return (
    <ModelContent
      path={modelPath}
      drops={drops}
      showStatusColors={showStatusColors}
      lowPerformanceMode={lowPerformanceMode}
    />
  );
}

function ModelContent({ path, drops, showStatusColors, lowPerformanceMode }) {
  const model = useGLTF(path);
  const { invalidate } = useThree();
  const scene = useMemo(() => model.scene.clone(true), [model.scene]);
  const sceneRef = useRef(scene);

  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  useEffect(() => {
    return () => {
      disposeSceneResources(sceneRef.current);
      useGLTF.clear(path);
    };
  }, [path]);

  useEffect(() => {
    if (!lowPerformanceMode) {
      return;
    }

    scene.traverse((node) => {
      if (!node?.isMesh || !node.material) {
        return;
      }

      const applyLowQualityMaterialSettings = (material) => {
        if (!material || SHARED_STATUS_MATERIALS.has(material)) {
          return;
        }
        material.flatShading = true;
        material.dithering = false;
        material.toneMapped = false;
        if (material.map) {
          material.map.anisotropy = 1;
          material.map.generateMipmaps = false;
          material.map.needsUpdate = true;
        }
        material.needsUpdate = true;
      };

      if (Array.isArray(node.material)) {
        node.material.forEach(applyLowQualityMaterialSettings);
      } else {
        applyLowQualityMaterialSettings(node.material);
      }
    });

    invalidate();
  }, [scene, lowPerformanceMode, invalidate]);

  useDropsMaterialUpdater({
    scene,
    drops,
    enabled: showStatusColors,
    invalidate,
  });

  return <primitive object={scene} />;
}
